import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
    Button,
    FormGroup,
    FormControl,
    InputGroup,
    Modal
} from "react-bootstrap";

import { 
    reactToExperience, 
    addComment, 
    deleteExperience, 
    deleteComment, 
    updateExperience,
    deleteOwnExperience,
    updateComment,
    deleteOwnComment
} from '../../../../redux/actions/experiences';
import { isAdmin } from 'helpers/permissions';
import { getUserDisplayName, getInitials, getAvatarColor } from 'helpers/userUtils';
import ExperienceEditModal from './ExperienceEditModal.jsx';

class ModernExperienceCard extends Component {
    constructor(props) {
        super(props);
        
        this.state = {
            showComments: false,
            newComment: '',
            showDeleteModal: false,
            deleteReason: '',
            showEditModal: false,
            editingCommentId: null,
            editingCommentText: '',
            showImageModal: false,
            selectedImageIndex: 0
        };
    }

    // Les utilitaires sont maintenant importés depuis helpers/userUtils

    handleReaction = (reactionType) => {
        const { experience, currentUser } = this.props;
        
        if (!currentUser) {
            if (this.props.showNotification) {
                this.props.showNotification("Veuillez vous connecter pour réagir", "warning");
            }
            return;
        }

        try {
            this.props.reactToExperience(experience.id, reactionType);
        } catch (error) {
            if (this.props.showNotification) {
                this.props.showNotification("Erreur lors de la réaction", "error");
            }
        }
    };

    handleToggleComments = () => {
        this.setState({ showComments: !this.state.showComments });
    };

    handleCommentChange = (e) => {
        this.setState({ newComment: e.target.value });
    };

    handleAddComment = async () => {
        const { experience, currentUser } = this.props;
        const { newComment } = this.state;
        
        if (!currentUser) {
            if (this.props.showNotification) {
                this.props.showNotification("Veuillez vous connecter pour commenter", "warning");
            }
            return;
        }

        if (!newComment.trim()) {
            return;
        }

        try {
            await this.props.addComment(experience.id, newComment.trim());
            this.setState({ newComment: '' });
        } catch (error) {
            if (this.props.showNotification) {
                this.props.showNotification("Erreur lors de l'ajout du commentaire", "error");
            }
        }
    };

    // Gérer l'édition d'expérience
    handleEditExperience = () => {
        this.setState({ showEditModal: true });
    };

    handleSaveExperience = async (updatedData) => {
        try {
            await this.props.updateExperience(this.props.experience.id, updatedData);
            this.setState({ showEditModal: false });
            if (this.props.showNotification) {
                this.props.showNotification("Expérience modifiée avec succès", "success");
            }
        } catch (error) {
            if (this.props.showNotification) {
                this.props.showNotification("Erreur lors de la modification", "error");
            }
        }
    };

    // Gérer la suppression d'expérience par l'auteur
    handleDeleteOwnExperience = async () => {
        try {
            await this.props.deleteOwnExperience(this.props.experience.id);
            if (this.props.showNotification) {
                this.props.showNotification("Expérience supprimée avec succès", "success");
            }
        } catch (error) {
            if (this.props.showNotification) {
                this.props.showNotification("Erreur lors de la suppression", "error");
            }
        }
    };

    // Gérer l'édition de commentaire
    handleEditComment = (commentId, currentText) => {
        this.setState({
            editingCommentId: commentId,
            editingCommentText: currentText
        });
    };

    handleSaveComment = async (commentId) => {
        const { editingCommentText } = this.state;
        if (!editingCommentText.trim()) return;

        try {
            await this.props.updateComment(this.props.experience.id, commentId, editingCommentText);
            this.setState({
                editingCommentId: null,
                editingCommentText: ''
            });
            if (this.props.showNotification) {
                this.props.showNotification("Commentaire modifié avec succès", "success");
            }
        } catch (error) {
            if (this.props.showNotification) {
                this.props.showNotification("Erreur lors de la modification du commentaire", "error");
            }
        }
    };

    handleCancelEditComment = () => {
        this.setState({
            editingCommentId: null,
            editingCommentText: ''
        });
    };

    // Gérer la suppression de commentaire par l'auteur
    handleDeleteOwnComment = async (commentId) => {
        try {
            await this.props.deleteOwnComment(this.props.experience.id, commentId);
            if (this.props.showNotification) {
                this.props.showNotification("Commentaire supprimé avec succès", "success");
            }
        } catch (error) {
            if (this.props.showNotification) {
                this.props.showNotification("Erreur lors de la suppression du commentaire", "error");
            }
        }
    };

    // Gérer l'affichage des images en grand
    handleImageClick = (imageIndex) => {
        this.setState({
            showImageModal: true,
            selectedImageIndex: imageIndex
        });
    };

    handleCloseImageModal = () => {
        this.setState({ showImageModal: false });
    };

    handlePrevImage = () => {
        const { selectedImageIndex } = this.state;
        const { experience } = this.props;
        const newIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : experience.images.length - 1;
        this.setState({ selectedImageIndex: newIndex });
    };

    handleNextImage = () => {
        const { selectedImageIndex } = this.state;
        const { experience } = this.props;
        const newIndex = selectedImageIndex < experience.images.length - 1 ? selectedImageIndex + 1 : 0;
        this.setState({ selectedImageIndex: newIndex });
    };

    formatTimeAgo = (dateString) => {
        const now = new Date();
        const date = new Date(dateString);
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));
        
        if (diffInMinutes < 1) return 'À l\'instant';
        if (diffInMinutes < 60) return `il y a ${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''}`;
        
        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `il y a ${diffInHours} heure${diffInHours > 1 ? 's' : ''}`;
        
        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) return `il y a ${diffInDays} jour${diffInDays > 1 ? 's' : ''}`;
        
        return date.toLocaleDateString('fr-FR');
    };

    render() {
        const { experience, currentUser } = this.props;
        const { 
            showComments, 
            newComment, 
            showDeleteModal, 
            deleteReason, 
            showEditModal,
            editingCommentId,
            editingCommentText,
            showImageModal,
            selectedImageIndex
        } = this.state;
        
        const userIsAdmin = isAdmin(currentUser);
        const isAuthor = currentUser && currentUser.uid === experience.author.id;
        const reactions = experience.reactions || {};
        const comments = experience.comments || [];
        const authorInitials = getInitials(experience.author.name);
        const avatarColor = getAvatarColor(experience.author.name);
        
        const totalReactions = Object.values(reactions).reduce((sum, count) => sum + count, 0);

        return (
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                border: '1px solid #e9ecef',
                transition: 'all 0.2s ease',
                cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
            }}
            >
                {/* En-tête avec avatar et infos utilisateur */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        backgroundColor: avatarColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '16px',
                        marginRight: '12px'
                    }}>
                        {authorInitials}
                    </div>
                    
                    <div style={{ flex: 1 }}>
                        <div style={{ 
                            fontWeight: '600',
                            fontSize: '16px',
                            color: '#2c3e50',
                            marginBottom: '2px'
                        }}>
                            {experience.author.name}
                        </div>
                        <div style={{ 
                            fontSize: '14px',
                            color: '#7f8c8d'
                        }}>
                            {this.formatTimeAgo(experience.createdAt)}
                        </div>
                    </div>

                    {/* Boutons d'action */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {/* Boutons pour l'auteur */}
                        {isAuthor && (
                            <>
                                <button
                                    onClick={this.handleEditExperience}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#3498db',
                                        fontSize: '16px',
                                        cursor: 'pointer',
                                        padding: '8px',
                                        borderRadius: '4px'
                                    }}
                                    title="Modifier"
                                >
                                    <i className="pe-7s-pen" />
                                </button>
                                <button
                                    onClick={this.handleDeleteOwnExperience}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#e74c3c',
                                        fontSize: '16px',
                                        cursor: 'pointer',
                                        padding: '8px',
                                        borderRadius: '4px'
                                    }}
                                    title="Supprimer"
                                >
                                    <i className="pe-7s-trash" />
                                </button>
                            </>
                        )}
                        
                        {/* Bouton de suppression pour les admins (si pas l'auteur) */}
                        {userIsAdmin && !isAuthor && (
                            <Button
                                bsStyle="link"
                                onClick={() => this.setState({ showDeleteModal: true })}
                                style={{ 
                                    padding: '8px',
                                    color: '#e74c3c',
                                    fontSize: '16px'
                                }}
                                title="Suppression administrative"
                            >
                                <i className="pe-7s-shield" />
                            </Button>
                        )}
                    </div>
                </div>

                {/* Titre de l'expérience */}
                <h3 style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#2c3e50',
                    marginBottom: '12px',
                    lineHeight: '1.3'
                }}>
                    {experience.title}
                </h3>

                {/* Contenu de l'expérience */}
                <div style={{
                    fontSize: '16px',
                    lineHeight: '1.6',
                    color: '#34495e',
                    marginBottom: '16px'
                }}>
                    {experience.content.length > 300 
                        ? experience.content.substring(0, 300) + '...'
                        : experience.content
                    }
                </div>

                {/* Images */}
                {experience.images && experience.images.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                        {experience.images.length === 1 ? (
                            <div style={{
                                borderRadius: '8px',
                                overflow: 'hidden',
                                maxWidth: '100%'
                            }}>
                                {experience.images[0] && experience.images[0].startsWith('data:') ? (
                                    <img
                                        src={experience.images[0]}
                                        alt="Image de l'expérience"
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            maxHeight: '300px',
                                            objectFit: 'cover',
                                            borderRadius: '8px',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => this.handleImageClick(0)}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                ) : null}
                                <div style={{
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                    padding: '40px',
                                    textAlign: 'center',
                                    border: '2px dashed #dee2e6',
                                    display: experience.images[0] && experience.images[0].startsWith('data:') ? 'none' : 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <i className="pe-7s-photo" style={{ 
                                        fontSize: '32px', 
                                        color: '#adb5bd',
                                        marginBottom: '8px'
                                    }} />
                                    <div style={{ 
                                        color: '#6c757d',
                                        fontSize: '14px'
                                    }}>
                                        Image de l'expérience
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '8px'
                            }}>
                                {experience.images.map((image, index) => (
                                    <div key={index} style={{
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        position: 'relative'
                                    }}>
                                        {image && image.startsWith('data:') ? (
                                            <img
                                                src={image}
                                                alt={`Image ${index + 1}`}
                                                style={{
                                                    width: '100%',
                                                    height: '150px',
                                                    objectFit: 'cover',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => this.handleImageClick(index)}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'flex';
                                                }}
                                            />
                                        ) : null}
                                        <div style={{
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '8px',
                                            padding: '30px',
                                            textAlign: 'center',
                                            border: '2px dashed #dee2e6',
                                            display: image && image.startsWith('data:') ? 'none' : 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '150px'
                                        }}>
                                            <i className="pe-7s-photo" style={{ 
                                                fontSize: '24px', 
                                                color: '#adb5bd',
                                                marginBottom: '4px'
                                            }} />
                                            <div style={{ 
                                                color: '#6c757d',
                                                fontSize: '12px'
                                            }}>
                                                Image {index + 1}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Actions (likes, commentaires, partager) */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: '16px',
                    borderTop: '1px solid #e9ecef'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        {/* Likes */}
                        <button
                            onClick={() => this.handleReaction('likes')}
                            style={{
                                background: 'none',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                color: '#7f8c8d',
                                fontSize: '14px',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                borderRadius: '20px',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                e.currentTarget.style.color = '#e67e22';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#7f8c8d';
                            }}
                        >
                            <i className="pe-7s-like" style={{ fontSize: '16px' }} />
                            <span>{reactions.likes || 0}</span>
                        </button>

                        {/* Commentaires */}
                        <button
                            onClick={this.handleToggleComments}
                            style={{
                                background: 'none',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                color: '#7f8c8d',
                                fontSize: '14px',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                borderRadius: '20px',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                e.currentTarget.style.color = '#e67e22';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#7f8c8d';
                            }}
                        >
                            <i className="pe-7s-comment" style={{ fontSize: '16px' }} />
                            <span>{comments.length}</span>
                        </button>

                        {/* Partager */}
                        <button
                            style={{
                                background: 'none',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                color: '#7f8c8d',
                                fontSize: '14px',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                borderRadius: '20px',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                e.currentTarget.style.color = '#e67e22';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#7f8c8d';
                            }}
                        >
                            <i className="pe-7s-share" style={{ fontSize: '16px' }} />
                            <span>Partager</span>
                        </button>
                    </div>

                    {/* Tag Expérience */}
                    <div style={{
                        backgroundColor: '#fff5f5',
                        color: '#e67e22',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '500',
                        border: '1px solid #ffeaa7'
                    }}>
                        Expérience
                    </div>
                </div>

                {/* Section des commentaires (expandable) */}
                {showComments && (
                    <div style={{
                        marginTop: '20px',
                        paddingTop: '20px',
                        borderTop: '1px solid #e9ecef'
                    }}>
                        {/* Formulaire d'ajout de commentaire */}
                        {currentUser && (
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                    <div style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        backgroundColor: getAvatarColor(getUserDisplayName(currentUser)),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontWeight: '600',
                                        fontSize: '12px',
                                        flexShrink: 0
                                    }}>
                                        {getInitials(getUserDisplayName(currentUser))}
                                    </div>
                                    
                                    <div style={{ flex: 1 }}>
                                        <FormControl
                                            type="text"
                                            placeholder="Ajouter un commentaire..."
                                            value={newComment}
                                            onChange={this.handleCommentChange}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    this.handleAddComment();
                                                }
                                            }}
                                            style={{
                                                border: '1px solid #e9ecef',
                                                borderRadius: '20px',
                                                padding: '8px 16px',
                                                fontSize: '14px'
                                            }}
                                        />
                                        {newComment.trim() && (
                                            <button
                                                onClick={this.handleAddComment}
                                                style={{
                                                    marginTop: '8px',
                                                    backgroundColor: '#e67e22',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '16px',
                                                    padding: '6px 16px',
                                                    fontSize: '12px',
                                                    fontWeight: '500',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Publier
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Liste des commentaires */}
                        {comments.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {comments.map(comment => {
                                    const isCommentAuthor = currentUser && currentUser.uid === comment.authorId;
                                    const isEditingThisComment = editingCommentId === comment.id;
                                    
                                    return (
                                        <div key={comment.id} style={{ display: 'flex', gap: '12px' }}>
                                            <div style={{
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '50%',
                                                backgroundColor: getAvatarColor(comment.author),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                fontWeight: '600',
                                                fontSize: '12px',
                                                flexShrink: 0
                                            }}>
                                                {getInitials(comment.author)}
                                            </div>
                                            
                                            <div style={{ flex: 1 }}>
                                                <div style={{
                                                    backgroundColor: '#f8f9fa',
                                                    borderRadius: '16px',
                                                    padding: '12px 16px'
                                                }}>
                                                    <div style={{
                                                        fontWeight: '600',
                                                        fontSize: '14px',
                                                        color: '#2c3e50',
                                                        marginBottom: '4px'
                                                    }}>
                                                        {comment.author}
                                                    </div>
                                                    
                                                    {/* Contenu du commentaire ou champ d'édition */}
                                                    {isEditingThisComment ? (
                                                        <div>
                                                            <FormControl
                                                                componentClass="textarea"
                                                                rows={2}
                                                                value={editingCommentText}
                                                                onChange={(e) => this.setState({ editingCommentText: e.target.value })}
                                                                style={{
                                                                    fontSize: '14px',
                                                                    border: '1px solid #e9ecef',
                                                                    borderRadius: '8px',
                                                                    padding: '8px 12px',
                                                                    marginBottom: '8px'
                                                                }}
                                                            />
                                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                                <button
                                                                    onClick={() => this.handleSaveComment(comment.id)}
                                                                    style={{
                                                                        backgroundColor: '#e67e22',
                                                                        color: 'white',
                                                                        border: 'none',
                                                                        borderRadius: '4px',
                                                                        padding: '4px 12px',
                                                                        fontSize: '12px',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    Enregistrer
                                                                </button>
                                                                <button
                                                                    onClick={this.handleCancelEditComment}
                                                                    style={{
                                                                        backgroundColor: 'transparent',
                                                                        color: '#6c757d',
                                                                        border: '1px solid #dee2e6',
                                                                        borderRadius: '4px',
                                                                        padding: '4px 12px',
                                                                        fontSize: '12px',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    Annuler
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div style={{
                                                            fontSize: '14px',
                                                            color: '#34495e',
                                                            lineHeight: '1.4'
                                                        }}>
                                                            {comment.content}
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                {/* Actions du commentaire */}
                                                {!isEditingThisComment && (
                                                    <div style={{
                                                        fontSize: '12px',
                                                        color: '#7f8c8d',
                                                        marginTop: '4px',
                                                        marginLeft: '16px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '12px'
                                                    }}>
                                                        <span>{this.formatTimeAgo(comment.createdAt)}</span>
                                                        
                                                        {/* Actions pour l'auteur du commentaire */}
                                                        {isCommentAuthor && (
                                                            <>
                                                                <button
                                                                    onClick={() => this.handleEditComment(comment.id, comment.content)}
                                                                    style={{
                                                                        background: 'none',
                                                                        border: 'none',
                                                                        color: '#3498db',
                                                                        fontSize: '12px',
                                                                        cursor: 'pointer',
                                                                        padding: 0
                                                                    }}
                                                                >
                                                                    Modifier
                                                                </button>
                                                                <button
                                                                    onClick={() => this.handleDeleteOwnComment(comment.id)}
                                                                    style={{
                                                                        background: 'none',
                                                                        border: 'none',
                                                                        color: '#e74c3c',
                                                                        fontSize: '12px',
                                                                        cursor: 'pointer',
                                                                        padding: 0
                                                                    }}
                                                                >
                                                                    Supprimer
                                                                </button>
                                                            </>
                                                        )}
                                                        
                                                        {/* Action admin (si pas l'auteur) */}
                                                        {userIsAdmin && !isCommentAuthor && (
                                                            <button
                                                                onClick={() => this.props.deleteComment(experience.id, comment.id, 'Suppression administrative')}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    color: '#e74c3c',
                                                                    fontSize: '12px',
                                                                    cursor: 'pointer',
                                                                    padding: 0
                                                                }}
                                                            >
                                                                Supprimer (Admin)
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                color: '#7f8c8d',
                                fontSize: '14px',
                                fontStyle: 'italic',
                                padding: '20px'
                            }}>
                                Aucun commentaire pour le moment
                            </div>
                        )}
                    </div>
                )}

                {/* Modal de suppression pour les admins */}
                <Modal show={showDeleteModal} onHide={() => this.setState({ showDeleteModal: false })}>
                    <Modal.Header closeButton>
                        <Modal.Title>Supprimer l'expérience</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Êtes-vous sûr de vouloir supprimer cette expérience ?</p>
                        <FormGroup>
                            <label>Motif de suppression</label>
                            <FormControl
                                componentClass="textarea"
                                rows={3}
                                value={deleteReason}
                                onChange={(e) => this.setState({ deleteReason: e.target.value })}
                                placeholder="Veuillez indiquer le motif..."
                            />
                        </FormGroup>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={() => this.setState({ showDeleteModal: false })}>
                            Annuler
                        </Button>
                        <Button 
                            bsStyle="danger"
                            onClick={async () => {
                                try {
                                    await this.props.deleteExperience(experience.id, deleteReason);
                                    this.setState({ showDeleteModal: false, deleteReason: '' });
                                } catch (error) {
                                }
                            }}
                        >
                            Supprimer
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal d'édition d'expérience */}
                <ExperienceEditModal
                    show={showEditModal}
                    onHide={() => this.setState({ showEditModal: false })}
                    experience={experience}
                    onSave={this.handleSaveExperience}
                />

                {/* Modal pour afficher les images en grand */}
                {experience.images && experience.images.length > 0 && (
                    <Modal
                        show={showImageModal}
                        onHide={this.handleCloseImageModal}
                        bsSize="large"
                        style={{ zIndex: 1060 }}
                    >
                        <Modal.Header closeButton style={{ backgroundColor: '#2c3e50', color: 'white' }}>
                            <Modal.Title>
                                Image {selectedImageIndex + 1} sur {experience.images.length}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body style={{ 
                            padding: 0, 
                            backgroundColor: '#000',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '400px',
                            position: 'relative'
                        }}>
                            {experience.images[selectedImageIndex] && (
                                <img
                                    src={experience.images[selectedImageIndex]}
                                    alt={`Image ${selectedImageIndex + 1}`}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '70vh',
                                        objectFit: 'contain'
                                    }}
                                />
                            )}
                            
                            {/* Boutons de navigation si plusieurs images */}
                            {experience.images.length > 1 && (
                                <>
                                    <button
                                        onClick={this.handlePrevImage}
                                        style={{
                                            position: 'absolute',
                                            left: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            backgroundColor: 'rgba(0,0,0,0.5)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '50%',
                                            width: '40px',
                                            height: '40px',
                                            cursor: 'pointer',
                                            fontSize: '18px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        ‹
                                    </button>
                                    <button
                                        onClick={this.handleNextImage}
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            backgroundColor: 'rgba(0,0,0,0.5)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '50%',
                                            width: '40px',
                                            height: '40px',
                                            cursor: 'pointer',
                                            fontSize: '18px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        ›
                                    </button>
                                </>
                            )}
                        </Modal.Body>
                        
                        {/* Miniatures si plusieurs images */}
                        {experience.images.length > 1 && (
                            <Modal.Footer style={{ 
                                backgroundColor: '#f8f9fa',
                                borderTop: '1px solid #e9ecef',
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '8px',
                                flexWrap: 'wrap'
                            }}>
                                {experience.images.map((image, index) => (
                                    <div
                                        key={index}
                                        onClick={() => this.setState({ selectedImageIndex: index })}
                                        style={{
                                            width: '60px',
                                            height: '40px',
                                            border: selectedImageIndex === index ? '3px solid #e67e22' : '2px solid #dee2e6',
                                            borderRadius: '4px',
                                            overflow: 'hidden',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <img
                                            src={image}
                                            alt={`Miniature ${index + 1}`}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </div>
                                ))}
                            </Modal.Footer>
                        )}
                    </Modal>
                )}
            </div>
        );
    }
}

const mapDispatchToProps = dispatch => ({
    reactToExperience: (experienceId, reactionType) => dispatch(reactToExperience(experienceId, reactionType)),
    addComment: (experienceId, commentText) => dispatch(addComment(experienceId, commentText)),
    deleteExperience: (experienceId, reason) => dispatch(deleteExperience(experienceId, reason)),
    deleteComment: (experienceId, commentId, reason) => dispatch(deleteComment(experienceId, commentId, reason)),
    updateExperience: (experienceId, updatedData) => dispatch(updateExperience(experienceId, updatedData)),
    deleteOwnExperience: (experienceId) => dispatch(deleteOwnExperience(experienceId)),
    updateComment: (experienceId, commentId, updatedText) => dispatch(updateComment(experienceId, commentId, updatedText)),
    deleteOwnComment: (experienceId, commentId) => dispatch(deleteOwnComment(experienceId, commentId))
});

ModernExperienceCard.propTypes = {
    experience: PropTypes.object.isRequired,
    currentUser: PropTypes.object,
    showNotification: PropTypes.func,
    reactToExperience: PropTypes.func.isRequired,
    addComment: PropTypes.func.isRequired,
    deleteExperience: PropTypes.func.isRequired,
    deleteComment: PropTypes.func.isRequired,
    updateExperience: PropTypes.func.isRequired,
    deleteOwnExperience: PropTypes.func.isRequired,
    updateComment: PropTypes.func.isRequired,
    deleteOwnComment: PropTypes.func.isRequired
};

export default connect(null, mapDispatchToProps)(ModernExperienceCard);